import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { MondayItems } from './MondayItems';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function MondayConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getMondayConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Monday.com evidence</DialogTitle>
        <DialogDescription>Collect visible accounts, roles and direct team memberships for access reviews.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Monday.com connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <p>Connected account: {status.data.providerAccount}</p> : null}
        <p className='text-muted-foreground'>Use Scan on the Monday.com card to collect evidence and view progress and results. Use Access reviews to review collected accounts and assignments. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>After saving boards, Scan also collects 30 days of visible change-event metadata for those boards. Column values, origin and outcome are excluded; this does not prove complete history or approval.</p>
        <p className='text-muted-foreground'>Collection includes API-visible active, inactive and pending accounts. Complete account visibility, custom-role permissions, MFA and SSO enforcement require separate review.</p>
        <p className='text-muted-foreground'>Screenata requests read access to your account, users, teams, boards and updates. Reconnect to approve board and update access if you connected before item collection was added.</p>
        {integrationId && status.data && !status.error ? <MondayItems key={integrationId} integrationId={integrationId} open={open} /> : null}
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Monday.com separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await MondayProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Monday.com' : 'Connect Monday.com'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const MondayProvider: IntegrationProvider = {
  id: 'Monday', name: 'Monday.com', description: 'Access inventory and selected boards, items and updates',
  Icon: () => <img src={vendorLogoPath('monday')} alt='' className='size-6 object-contain' />,
  ConfigModal: MondayConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'access', name: 'Access inventory', description: 'Read visible users, roles and direct team memberships', enabled: true },
    { id: 'activity', name: 'Board activity', description: 'Read 30 days of event metadata for saved boards', enabled: true },
    { id: 'items', name: 'Selected board items', description: 'Read active items, subitems and visible updates and replies', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initMondayAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Monday.com authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'monday-auth', provider: 'Monday', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Monday.com.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeMondayAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getMondayConnectionStatus.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getMondayCollectionStatus.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getMondayBoards.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getMondayItems.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Monday.com integration card to collect access evidence.'); },
};
